import { http } from '@/api/http'
import type { AdminMetrics } from '@/lib/pos-types'
import { normalizeStationTicket, unwrapList, unwrapSingle } from '@/api/normalizers'

/**
 * API Admin Metrics.
 *
 * Este endpoint existe para evitar que el dashboard cargue “listas completas”
 * y mejorar performance/polling.
 *
 * Fuente: GET /api/admin/metrics (requiere admin).
 * - Devuelve contadores (órdenes/mesas)
 * - Devuelve tickets fallidos (últimas N horas)
 */
export const adminMetricsApi = {
  get: async (params?: { station?: string; sinceHours?: number; limit?: number }): Promise<AdminMetrics> => {
    const qs = new URLSearchParams()
    if (params?.station) qs.set('station', params.station)
    if (typeof params?.sinceHours === 'number') qs.set('since_hours', String(params.sinceHours))
    if (typeof params?.limit === 'number') qs.set('limit', String(params.limit))
    const res = await http.get<unknown>(`/admin/metrics${qs.toString() ? `?${qs.toString()}` : ''}`)
    const data = unwrapSingle(res.data) as Record<string, unknown>

    const failedTicketsRaw = unwrapList((data as { failedTickets?: unknown; failed_tickets?: unknown }).failedTickets ?? (data as { failed_tickets?: unknown }).failed_tickets)
    const failedTickets = failedTicketsRaw.map(normalizeStationTicket)

    const countsRaw = (data as { failedTicketCounts?: unknown; failed_ticket_counts?: unknown }).failedTicketCounts ?? (data as { failed_ticket_counts?: unknown }).failed_ticket_counts
    const failedTicketCounts: Record<string, number> =
      countsRaw && typeof countsRaw === 'object' && !Array.isArray(countsRaw)
        ? Object.fromEntries(Object.entries(countsRaw as Record<string, unknown>).map(([k, v]) => [k, typeof v === 'number' ? v : Number(v) || 0]))
        : {}

    const dishesRaw = unwrapList((data as { dishesReadyToday?: unknown; dishes_ready_today?: unknown }).dishesReadyToday ?? (data as { dishes_ready_today?: unknown }).dishes_ready_today)
    const dishesReadyList = dishesRaw.map((d) => {
      const item = d as Record<string, unknown>
      return {
        name: String(item.name ?? item.product_name ?? ''),
        quantity: Number(item.quantity) || 0,
      }
    })

    const dishesReadyTotalCount = Number(
      (data as { dishesReadyTodayTotal?: unknown; dishes_ready_today_total?: unknown }).dishesReadyTodayTotal ??
        (data as { dishes_ready_today_total?: unknown }).dishes_ready_today_total
    ) || dishesReadyList.reduce((acc, curr) => acc + curr.quantity, 0)

    return {
      sinceHours: Number((data as { sinceHours?: unknown; since_hours?: unknown }).sinceHours ?? (data as { since_hours?: unknown }).since_hours) || 24,
      ordersOpenCount: Number((data as { ordersOpenCount?: unknown; orders_open_count?: unknown }).ordersOpenCount ?? (data as { orders_open_count?: unknown }).orders_open_count) || 0,
      ordersPendingPaymentCount:
        Number((data as { ordersPendingPaymentCount?: unknown; orders_pending_payment_count?: unknown }).ordersPendingPaymentCount ?? (data as { orders_pending_payment_count?: unknown }).orders_pending_payment_count) || 0,
      tablesOccupiedCount: Number((data as { tablesOccupiedCount?: unknown; tables_occupied_count?: unknown }).tablesOccupiedCount ?? (data as { tables_occupied_count?: unknown }).tables_occupied_count) || 0,
      tablesPendingPaymentCount:
        Number((data as { tablesPendingPaymentCount?: unknown; tables_pending_payment_count?: unknown }).tablesPendingPaymentCount ?? (data as { tables_pending_payment_count?: unknown }).tables_pending_payment_count) || 0,
      tablesBlockedCount: Number((data as { tablesBlockedCount?: unknown; tables_blocked_count?: unknown }).tablesBlockedCount ?? (data as { tables_blocked_count?: unknown }).tables_blocked_count) || 0,
      dishesReadyTotalCount,
      dishesReadyList,
      failedTicketCounts,
      failedTickets,
    }
  },
}
